import { z } from "zod";
import { clockEventKindSchema, eventSourceSchema, instantSchema, localDateSchema } from "./schemas";

/*
 * Runtime schemas of the location captured when clocking (T-175, ADR-0144). They mirror
 * `backend/openapi.json` like `schemas.ts`.
 */

/** Legal basis the employer names for the capture (no consent in an employment relationship). */
export const locationLegalBasisSchema = z.enum(["LegitimateInterest", "WorksAgreement"]);

/** `GET /me/location-capture`: whether the app asks for the location when the person clocks. */
export const myLocationCaptureSchema = z.object({
  enabled: z.boolean(),
  purpose: z.string().nullable(),
  legalBasis: locationLegalBasisSchema.nullable(),
  legalBasisReference: z.string().nullable(),
  retentionDays: z.number().int().nullable(),
  changedAt: instantSchema.nullable(),
});

/** `GET` and `PUT /tenants/{id}/location-capture`. */
export const tenantLocationCaptureSchema = z.object({
  enabled: z.boolean(),
  entitled: z.boolean(),
  active: z.boolean(),
  purpose: z.string().nullable(),
  legalBasis: locationLegalBasisSchema.nullable(),
  legalBasisReference: z.string().nullable(),
  retentionDays: z.number().int().nullable(),
  maxRetentionDays: z.number().int(),
  changedAt: instantSchema.nullable(),
});

/**
 * Coordinates of an establishment (`GET /establishment-locations`) with the radius of the geofence
 * reminder (T-188); a server before T-188 sends no radius.
 */
export const establishmentLocationSchema = z.object({
  establishmentId: z.uuid(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  geofenceRadiusMeters: z.number().int().nullable().optional(),
});

/** `GET /establishment-locations`. */
export const establishmentLocationsSchema = z.array(establishmentLocationSchema);

/** The location of one clock event in the day detail. */
export const clockLocationSchema = z.object({
  eventId: z.uuid(),
  kind: clockEventKindSchema,
  occurredAt: instantSchema,
  timeZoneId: z.string(),
  latitude: z.number(),
  longitude: z.number(),
  source: eventSourceSchema,
  distanceKilometers: z.number().int().nullable(),
});

/** `GET /employees/{id}/clock-locations`. */
export const clockLocationDaySchema = z.object({
  employeeId: z.uuid(),
  date: localDateSchema,
  establishmentId: z.uuid().nullable(),
  establishmentHasCoordinates: z.boolean(),
  locations: z.array(clockLocationSchema),
});

/** Legal basis of the location capture. */
export type LocationLegalBasis = z.infer<typeof locationLegalBasisSchema>;
/** The location capture as the app of the person sees it. */
export type MyLocationCapture = z.infer<typeof myLocationCaptureSchema>;
/** The location capture as the tenant administrator sees it. */
export type TenantLocationCapture = z.infer<typeof tenantLocationCaptureSchema>;
/** Coordinates of an establishment. */
export type EstablishmentLocation = z.infer<typeof establishmentLocationSchema>;
/** The locations of a person on a day. */
export type ClockLocationDay = z.infer<typeof clockLocationDaySchema>;

/** Query keys of the location capture. */
export const locationQueryKeys = {
  mine: ["me", "location-capture"] as const,
  tenant: (tenantId: string) => ["tenant", tenantId, "location-capture"] as const,
  establishments: ["master-data", "establishment-locations"] as const,
  day: (employeeId: string, date: string) => ["clock-locations", employeeId, date] as const,
};
